import { z } from 'zod';
import {
  ActiveStatusSchema,
  AdministrativeSectorSchema,
  EducationLevelSchema,
  RoleSchema,
} from './enums';
import { InepCodeSchema, PersonDocumentSchema, PersonNameSchema } from './validation';

export const GradingPolicySchema = z.object({
  /** Média mínima para aprovação. */
  passingScore: z.number().min(0).max(10).default(7),
  /** Abaixo de passingScore e >= recoveryMinScore → recuperação; abaixo → atenção. */
  recoveryMinScore: z.number().min(0).max(10).default(6),
  /** Falta justificada conta como presença no cálculo de frequência. */
  justifiedAbsenceCountsAsPresent: z.boolean().default(true),
  /** Frequência mínima esperada (0–1) usada em alertas. */
  minAttendanceRate: z.number().min(0).max(1).default(0.9),
});
export type GradingPolicy = z.infer<typeof GradingPolicySchema>;

export const DEFAULT_GRADING_POLICY: GradingPolicy = {
  passingScore: 7,
  recoveryMinScore: 6,
  justifiedAbsenceCountsAsPresent: true,
  minAttendanceRate: 0.9,
};

// ---------------------------------------------------------------- perfil (Censo Escolar / INEP)
/** Texto opcional: vazio vira null (valores como publicados no Catálogo de Escolas do INEP). */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo de ${max} caracteres`)
    .nullish()
    .transform((v) => (v ? v : null));

/**
 * Dados complementares da escola, no formato do Catálogo de Escolas do INEP.
 * Preenchidos automaticamente pela consulta do código INEP e editáveis no cadastro.
 */
export const SchoolProfileSchema = z.object({
  uf: z
    .string()
    .trim()
    .nullish()
    .transform((v) => (v ? v.toUpperCase() : null))
    .refine((v) => v === null || /^[A-Z]{2}$/.test(v), 'UF deve ter 2 letras (ex.: BA)'),
  city: optionalText(120),
  cep: z
    .string()
    .trim()
    .nullish()
    .transform((v) => (v ?? '').replace(/\D/g, '') || null)
    .refine((v) => v === null || v.length === 8, 'CEP deve ter 8 dígitos')
    .transform((v) => (v ? `${v.slice(0, 5)}-${v.slice(5)}` : null)),
  phone: optionalText(40),
  /** Urbana / Rural. */
  location: optionalText(40),
  /** Ex.: "A escola não está em área de localização diferenciada", "Terra indígena". */
  differentiatedLocation: optionalText(200),
  /** Federal, Estadual, Municipal ou Privada. */
  dependency: optionalText(40),
  /** Particular, Comunitária, Confessional, Filantrópica ou "Não Informado". */
  privateCategory: optionalText(80),
  /** Conveniada com o poder público (Sim/Não). */
  publicAgreement: optionalText(40),
  /** Regulamentação pelo Conselho de Educação (Sim/Não/Em tramitação). */
  regulation: optionalText(80),
  /** Porte da escola (faixa de matrículas). */
  size: optionalText(120),
  /** Etapas e modalidades de ensino oferecidas (como no INEP). */
  stages: z
    .array(z.string().trim().min(1).max(120))
    .max(30)
    .nullish()
    .transform((v) => v ?? []),
  /** Restrição de atendimento / situação de funcionamento. */
  situation: optionalText(200),
});
export type SchoolProfileInput = z.input<typeof SchoolProfileSchema>;
export type SchoolProfile = z.output<typeof SchoolProfileSchema>;

export const EMPTY_SCHOOL_PROFILE: SchoolProfile = {
  uf: null,
  city: null,
  cep: null,
  phone: null,
  location: null,
  differentiatedLocation: null,
  dependency: null,
  privateCategory: null,
  publicAgreement: null,
  regulation: null,
  size: null,
  stages: [],
  situation: null,
};

export const CreateSchoolSchema = z.object({
  inepCode: InepCodeSchema,
  name: z.string().trim().min(3, 'Informe o nome da escola').max(160),
  director: z.object({
    name: PersonNameSchema,
    document: PersonDocumentSchema.optional(),
  }),
  address: z.object({
    fullAddress: z.string().trim().min(5, 'Informe o endereço completo').max(300),
  }),
  administrativeSector: AdministrativeSectorSchema,
  educationLevels: z
    .array(EducationLevelSchema)
    .min(1, 'Selecione ao menos um nível de ensino')
    .transform((levels) => [...new Set(levels)]),
  profile: SchoolProfileSchema.optional(),
});
export type CreateSchoolInput = z.input<typeof CreateSchoolSchema>;
export type CreateSchoolData = z.output<typeof CreateSchoolSchema>;

export const UpdateSchoolSchema = CreateSchoolSchema.partial().extend({
  status: ActiveStatusSchema.optional(),
  gradingPolicy: GradingPolicySchema.partial().optional(),
});
export type UpdateSchoolInput = z.input<typeof UpdateSchoolSchema>;

export const SchoolSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  code: z.string(),
  inepCode: z.string().nullable(),
  name: z.string(),
  director: z.object({
    name: z.string(),
    /** Documento sempre mascarado na resposta. */
    documentMasked: z.string().nullable(),
  }),
  address: z.object({ fullAddress: z.string() }),
  administrativeSector: AdministrativeSectorSchema,
  educationLevels: z.array(EducationLevelSchema),
  /** Dados do Censo Escolar (INEP). */
  profile: z.object({
    uf: z.string().nullable(),
    city: z.string().nullable(),
    cep: z.string().nullable(),
    phone: z.string().nullable(),
    location: z.string().nullable(),
    differentiatedLocation: z.string().nullable(),
    dependency: z.string().nullable(),
    privateCategory: z.string().nullable(),
    publicAgreement: z.string().nullable(),
    regulation: z.string().nullable(),
    size: z.string().nullable(),
    stages: z.array(z.string()),
    situation: z.string().nullable(),
  }),
  /** Validada no INEP no cadastro (e a fonte usada). */
  inepVerified: z.boolean(),
  inepSource: z.enum(['INEP_CATALOG', 'SIMCAQ', 'NONE']).nullable(),
  status: ActiveStatusSchema,
  gradingPolicy: GradingPolicySchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Papel do usuário atual nesta escola. */
  myRole: RoleSchema.nullable(),
});
export type School = z.infer<typeof SchoolSchema>;
